import type { ReactElement } from 'react'
import { CreditCard, Key, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Entitlement, XeroInvoiceState } from '../../types'
import {
  buildLicenseDetail,
  buildPaymentDetail,
  getActivationStatus,
  getGateStatus,
} from './dual-gate-status/dual-gate-status.helpers'
import { ActionButtons, GateNode, SummaryGrid } from './dual-gate-status/dual-gate-status.parts'

interface DualGateStatusProps {
  entitlement: Entitlement
  xeroInvoiceState?: XeroInvoiceState | null
  licenseFingerprint?: string | null
  onPassPaymentGate?: () => void
  onPassLicenseGate?: () => void
  onActivate?: () => void
  isLoading?: boolean
}

export function DualGateStatus({
  entitlement,
  xeroInvoiceState,
  licenseFingerprint,
  onPassPaymentGate,
  onPassLicenseGate,
  onActivate,
  isLoading = false,
}: DualGateStatusProps): ReactElement {
  const { t } = useTranslation('common')
  const paymentGateStatus = getGateStatus(entitlement.paymentGatePassed)
  const licenseGateStatus = getGateStatus(entitlement.licenseGatePassed)
  const activationStatus = getActivationStatus(entitlement)

  const paymentDetail = buildPaymentDetail(xeroInvoiceState)
  const licenseDetail = buildLicenseDetail({ fingerprint: licenseFingerprint, t })

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-800">
      <h3 className="mb-6 text-lg font-semibold text-stone-900 dark:text-white">
        {t('dualGate.activationStatus')}
      </h3>

      <div className="flex items-start justify-center">
        <GateNode
          label={t('dualGate.paymentGate')}
          status={paymentGateStatus}
          icon={<CreditCard className="h-6 w-6" />}
          detail={paymentDetail}
          t={t}
        />
        <GateNode
          label={t('dualGate.licenseGate')}
          status={licenseGateStatus}
          icon={<Key className="h-6 w-6" />}
          detail={licenseDetail}
          t={t}
        />
        <GateNode
          label={t('dualGate.activation')}
          status={activationStatus}
          icon={<Shield className="h-6 w-6" />}
          detail={entitlement.status === 'active' ? t('dualGate.active') : t('dualGate.pending')}
          timestamp={entitlement.activatedAt}
          isLast
          t={t}
        />
      </div>

      <SummaryGrid entitlement={entitlement} t={t} />
      <ActionButtons
        entitlement={entitlement}
        isLoading={isLoading}
        onPassPaymentGate={onPassPaymentGate}
        onPassLicenseGate={onPassLicenseGate}
        onActivate={onActivate}
        t={t}
      />
    </div>
  )
}
